import { useState } from "react";
import { DownloadSimple, UploadSimple, CheckCircle, XCircle, FloppyDisk } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

export default function NomorSiswaImport() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/admin/nomor-template.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = "Template_Nomor_Siswa.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Gagal mengunduh template"); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setBusy(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await api.post(`/admin/nomor/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Gagal membaca file"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const saveAll = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const rows = preview.rows.filter((r) => r.ok);
      const res = await api.post(`/admin/nomor/commit`, { rows });
      toast.success(`${res.data.updated} Nomor Siswa tersimpan${res.data.skipped ? `, ${res.data.skipped} dilewati` : ""}`);
      setPreview(null); setFileName("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Gagal menyimpan"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Isi Nomor Siswa (Massal)" />
      <div className="px-5 pt-4 pb-28">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-4 text-xs text-amber-800">
          Unduh template — sudah berisi seluruh siswa. Isi kolom <b>Nomor Siswa</b> di sebelah nama, lalu unggah kembali. Nomor Siswa dipakai sebagai <b>username login orang tua</b>.
        </div>

        <button data-testid="nomor-download-template" onClick={downloadTemplate}
          className="w-full mb-3 border-2 border-primary text-primary font-semibold py-3 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
          <DownloadSimple size={18} weight="bold" /> Download Template (berisi siswa)
        </button>

        <label data-testid="nomor-upload-label" className="block w-full mb-4 bg-primary text-white font-semibold py-3.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform cursor-pointer">
          <UploadSimple size={18} weight="bold" /> {busy && !preview ? "Membaca..." : "Unggah Excel (.xlsx)"}
          <input data-testid="nomor-file-input" type="file" accept=".xlsx" className="hidden" onChange={onUpload} disabled={busy} />
        </label>
        {fileName && <p className="text-xs text-stone-400 -mt-2 mb-4">File: {fileName}</p>}

        {preview && (
          <div data-testid="nomor-preview">
            <div className="flex items-center gap-3 mb-3 text-sm">
              <span className="flex items-center gap-1 text-green-600 font-semibold"><CheckCircle size={16} weight="fill" /> {preview.valid} valid</span>
              <span className="flex items-center gap-1 text-red-500 font-semibold"><XCircle size={16} weight="fill" /> {preview.total - preview.valid} bermasalah</span>
            </div>
            <div className="rounded-2xl overflow-x-auto border border-stone-200 shadow-sm mb-4">
              <table className="w-full border-collapse text-xs min-w-[520px]">
                <thead>
                  <tr className="bg-stone-800 text-white text-left">
                    <th className="px-2 py-2 font-bold">#</th>
                    <th className="px-2 py-2 font-bold whitespace-nowrap">NIS</th>
                    <th className="px-2 py-2 font-bold whitespace-nowrap">Nama</th>
                    <th className="px-2 py-2 font-bold whitespace-nowrap">Kelas</th>
                    <th className="px-2 py-2 font-bold whitespace-nowrap">Nomor Siswa</th>
                    <th className="px-2 py-2 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => (
                    <tr key={i} data-testid={`nomor-row-${i}`} className={r.ok ? (i % 2 ? "bg-white" : "bg-green-50") : "bg-red-50"}>
                      <td className="px-2 py-2 border-t border-stone-100 text-stone-400">{i + 1}</td>
                      <td className="px-2 py-2 border-t border-stone-100 whitespace-nowrap">{r.nis || "-"}</td>
                      <td className="px-2 py-2 border-t border-stone-100 whitespace-nowrap">{r.name || "-"}</td>
                      <td className="px-2 py-2 border-t border-stone-100 whitespace-nowrap">{r.kelas || "-"}</td>
                      <td className="px-2 py-2 border-t border-stone-100 whitespace-nowrap font-semibold">{r.student_number || "-"}</td>
                      <td className="px-2 py-2 border-t border-stone-100">
                        {r.ok ? <span className="text-green-600 font-semibold">OK</span> : <span className="text-red-500">{r.errors.join(", ")}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {preview && preview.valid > 0 && (
        <div className="fixed bottom-0 left-0 right-0 md:left-auto md:w-[26rem] p-4 bg-gradient-to-t from-white via-white to-transparent">
          <button data-testid="nomor-save-all" onClick={saveAll} disabled={busy}
            className="w-full bg-primary text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60">
            <FloppyDisk size={18} weight="bold" /> {busy ? "Menyimpan..." : `Simpan Nomor Siswa (${preview.valid})`}
          </button>
        </div>
      )}
    </div>
  );
}
